import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Form, Spinner, InputGroup, ListGroup, Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import type { UserOrganisation, Supplier } from '../../types/types';
import { useGetSuppliers } from '../../hooks/useGetSuppliers';

interface SupplierSelectProps {
  onSelectionChange: (selectedSuppliers: string[]) => void;
}

export const SupplierSelect: React.FC<SupplierSelectProps> = ({
  onSelectionChange,
}) => {
  const selectedOrganisation = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.organisation.selected
  ) as UserOrganisation | undefined;

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [suppliers, setSuppliers] = useState<Partial<Supplier>[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [hasMore, setHasMore] = useState(true);

  const observer = useRef<IntersectionObserver | null>(null);
  const pageSize = 10;
  const orgId = selectedOrganisation?.organisation?.id ?? '';

  const {
    suppliers: fetchedSuppliers,
    loading,
    error,
    total,
  } = useGetSuppliers(orgId, {
    page,
    pageSize,
    search: debouncedSearch,
    sortBy: 'updated_at',
    sortDirection: 'desc',
  });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (search === debouncedSearch) return; // No change

      setDebouncedSearch(search);
      setPage(1);
      // Only clear if searching
      if (search) {
        console.log('resetting suppliers on search');
        setSuppliers([]);
      }
      setHasMore(true);
    }, 500);
    return () => clearTimeout(timer);
  }, [search, debouncedSearch]);

  // Reset on org change
  useEffect(() => {
    if (!orgId) return; // Skip on initial empty orgId
    setPage(1);
    console.log('resetting suppliers on org change');
    setSuppliers([]);
    setHasMore(true);
  }, [orgId]);

  // Append new suppliers
  useEffect(() => {
    if (!fetchedSuppliers) return;

    console.log(
      'fetchedSuppliers arrived:',
      fetchedSuppliers.length,
      'total:',
      total
    );

    setSuppliers((prev) => {
      const newList =
        page === 1 ? fetchedSuppliers : [...prev, ...fetchedSuppliers];
      const unique = Array.from(
        new Map(newList.map((s) => [s?.id, s])).values()
      );
      setHasMore(unique.length < total);
      return unique;
    });
  }, [fetchedSuppliers, page, total]);

  // Infinite scroll: observe last list item
  const lastElementRef = useCallback(
    (node: HTMLElement | null) => {
      if (loading || !hasMore) return;
      observer.current?.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          setPage((p) => p + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  const toggleSelection = (supplier: Partial<Supplier>) => {
    if (!supplier?.id) return;
    const newIds = selectedIds.includes(supplier.id)
      ? selectedIds.filter((id) => id !== supplier.id)
      : [...selectedIds, supplier.id];
    setSelectedIds(newIds);
    onSelectionChange(newIds);
  };

  if (!selectedOrganisation) {
    return <div>Please select an organisation first.</div>;
  }

  return (
    <>
      <div
        style={{
          position: 'sticky',
          top: 0,
          background: '#fff',
          padding: '0.5rem',
          borderTop: '1px solid #dee2e6',
          zIndex: 10,
        }}
      >
        <InputGroup>
          <Form.Control
            placeholder="Search suppliers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button
            variant="outline-secondary"
            onClick={() => setSearch('')}
            disabled={!search}
          >
            X
          </Button>
        </InputGroup>
      </div>

      <div style={{ height: '400px', overflowY: 'auto' }}>
        {loading && suppliers.length === 0 && (
          <div className="d-flex justify-content-center py-4">
            <Spinner animation="border" />
          </div>
        )}

        {error && <div className="text-danger text-center">Error: {error}</div>}

        {!loading && suppliers.length === 0 && !error && (
          <div className="text-muted text-center py-4">No suppliers found.</div>
        )}

        <ListGroup as="ol">
          {suppliers.map((supplier, i) => {
            if (!supplier?.id) return null;

            const isLast = i === suppliers.length - 1;

            return (
              <ListGroup.Item
                key={supplier.id}
                as={isLast ? 'div' : undefined}
                ref={isLast ? lastElementRef : null}
                className="d-flex align-items-start"
              >
                <Form.Check
                  type="checkbox"
                  className="me-2"
                  checked={selectedIds.includes(supplier.id)}
                  onChange={() => toggleSelection(supplier)}
                  label={`${supplier.company_name ?? 'Unknown'} - ${
                    supplier.registration_number ?? 'N/A'
                  }`}
                />
              </ListGroup.Item>
            );
          })}
        </ListGroup>

        {loading && suppliers.length > 0 && (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
          </div>
        )}
      </div>
    </>
  );
};
